import React, { useState, useEffect, useCallback } from 'react';
import { buildApiUrl } from '../../utils/apiBase.js';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import { AdminFormPageShimmer, AdminTablePageShimmer, AdminTableShimmer } from '../common/Shimmer.jsx';
import { useAdminDialog } from '../../contexts/AdminDialogContext.jsx';
import { useDestructiveAction } from '../../contexts/DestructiveActionContext.jsx';
import { IconChevronDown, IconChevronUp } from '../common/AdminIcons.jsx';
import { useScreenProbe } from '../../utils/screenTiming.js';

const DOC_TYPE_LABELS = { terms: '利用規約', privacy: 'プライバシーポリシー', commerce: '特定商取引法に基づく表記' };

const S = {
  card: {
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-lg)',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    background: 'var(--color-bg-secondary)',
  },
  cardTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    marginBottom: '1rem',
    color: 'var(--color-text-primary)',
  },
  label: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 600,
    marginBottom: '4px',
    color: 'var(--color-text-secondary)',
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
    color: 'var(--color-text-primary)',
    background: 'var(--color-bg-tertiary)',
  },
  textarea: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    fontSize: '0.9rem',
    resize: 'vertical',
    boxSizing: 'border-box',
    color: 'var(--color-text-primary)',
    background: 'var(--color-bg-tertiary)',
    lineHeight: 1.6,
    fontFamily: 'inherit',
  },
  btnPrimary: (disabled) => ({
    padding: '8px 24px',
    background: disabled ? 'var(--color-bg-hover)' : 'var(--color-accent-secondary)',
    color: disabled ? 'var(--color-text-muted)' : '#fff',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
  }),
  btnOutline: (disabled) => ({
    padding: '5px 14px',
    background: 'transparent',
    color: disabled ? 'var(--color-text-muted)' : 'var(--color-accent-secondary)',
    border: `1.5px solid ${disabled ? 'var(--color-border)' : 'var(--color-accent-secondary)'}`,
    borderRadius: 'var(--radius-md)',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    whiteSpace: 'nowrap',
  }),
  btnDanger: (disabled) => ({
    padding: '5px 14px',
    background: disabled ? 'var(--color-bg-hover)' : 'var(--color-accent-danger)',
    color: disabled ? 'var(--color-text-muted)' : '#fff',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    whiteSpace: 'nowrap',
  }),
  btnNotify: (disabled) => ({
    padding: '6px 16px',
    background: disabled ? 'var(--color-bg-hover)' : 'var(--color-accent-warning)',
    color: disabled ? 'var(--color-text-muted)' : '#fff',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    whiteSpace: 'nowrap',
  }),
  btnSmall: {
    padding: '4px 10px',
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    border: 'none',
    whiteSpace: 'nowrap',
  },
  badge: (bg, fg) => ({
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 'var(--radius-full)',
    fontSize: '0.75rem',
    fontWeight: 600,
    background: bg,
    color: fg,
  }),
  meta: { fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '6px' },
  tab: (active) => ({
    padding: '8px 20px',
    fontSize: '0.9rem',
    fontWeight: active ? 700 : 500,
    border: 'none',
    borderBottom: active ? '2px solid var(--color-accent-secondary)' : '2px solid transparent',
    background: 'transparent',
    color: active ? 'var(--color-accent-secondary)' : 'var(--color-text-secondary)',
    cursor: 'pointer',
    transition: 'all 0.15s',
  }),
  articleCard: {
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    padding: '1rem',
    marginBottom: '0.75rem',
    background: 'var(--color-bg-tertiary)',
  },
  articleHandle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '0.75rem',
  },
  orderBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: 'var(--radius-full)',
    background: 'var(--color-accent-secondary)',
    color: '#fff',
    fontSize: '0.75rem',
    fontWeight: 700,
    flexShrink: 0,
  },
};

const AdminLegalDocuments = () => {
  const probe = useScreenProbe('legal');
  const { showConfirm } = useAdminDialog();
  const { requestDestructiveAction } = useDestructiveAction();
  const [docType, setDocType] = useState('terms');
  const [articles, setArticles] = useState([]);
  const [meta, setMeta] = useState({});
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const [diffPreview, setDiffPreview] = useState(null);
  const [diffPreviewError, setDiffPreviewError] = useState('');
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [versionForm, setVersionForm] = useState({
    changeType: 'minor', effectiveDate: '', diffSummary: '', publishNow: false,
  });

  const fetchArticles = useCallback(async () => {
    probe.start('load');
    setLoading(true);
    try {
      const res = await fetch(buildApiUrl(`/api/admin/legal-documents/${docType}/articles`), { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || '条文の取得に失敗しました');
      setArticles(data.articles || []);
      setMeta({
        title: data.title,
        lastPublishedAt: data.lastPublishedAt,
        lastPublishedVersion: data.lastPublishedVersion,
        publishedVersionId: data.publishedVersionId,
      });
      probe.end('load', 'ok');
      probe.unlock();
    } catch (err) {
      probe.end('load', `error: ${err?.message || 'unknown'}`);
      setMsg({ type: 'error', text: err.message || '条文の読み込みに失敗しました' });
    } finally {
      setLoading(false);
    }
  }, [docType, probe]);

  const fetchVersions = useCallback(async () => {
    setVersionsLoading(true);
    try {
      const res = await fetch(buildApiUrl(`/api/admin/legal-documents/${docType}/versions`), { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'バージョン履歴の取得に失敗しました');
      setVersions(data.versions || []);
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'バージョン履歴の読み込みに失敗しました' });
    } finally {
      setVersionsLoading(false);
    }
  }, [docType]);

  const fetchDiffPreview = useCallback(async () => {
    setDiffPreviewError('');
    try {
      const res = await fetch(buildApiUrl(`/api/admin/legal-documents/${docType}/diff-preview`), { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || '差分プレビューの取得に失敗しました');
      setDiffPreview(data);
    } catch (err) {
      setDiffPreview(null);
      setDiffPreviewError(err.message || '差分プレビューの取得に失敗しました');
    }
  }, [docType]);

  useEffect(() => {
    fetchArticles();
    fetchVersions();
    setDiffPreview(null);
    setMsg({ type: '', text: '' });
    setShowVersionModal(false);
  }, [docType, fetchArticles, fetchVersions]);

  useEffect(() => {
    if (!loading && articles.length > 0) fetchDiffPreview();
  }, [loading, articles.length, fetchDiffPreview]);

  const showMsg = (type, text) => setMsg({ type, text });

  // ============================================================
  // 条文 CRUD
  // ============================================================

  const handleAddArticle = async () => {
    setActionLoading(true);
    setMsg({ type: '', text: '' });
    try {
      const res = await fetch(buildApiUrl(`/api/admin/legal-documents/${docType}/articles`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ heading: '新しい条文', body: '' }),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      await fetchArticles();
    } catch (err) {
      showMsg('error', err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateArticle = async (id, updates) => {
    try {
      const res = await fetch(buildApiUrl(`/api/admin/legal-documents/${docType}/articles/${id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error((await res.json()).message);
    } catch (err) {
      showMsg('error', err.message);
    }
  };

  const handleDeleteArticle = async (id, heading) => {
    const result = await requestDestructiveAction({
      title: '条文の削除',
      message: `「${heading}」を削除します。この操作は取り消せません。`,
      confirmLabel: '削除する',
    });
    if (!result?.reason) return;
    setActionLoading(true);
    setMsg({ type: '', text: '' });
    try {
      const res = await fetch(buildApiUrl(`/api/admin/legal-documents/${docType}/articles/${id}`), {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: result.reason }),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      await fetchArticles();
      showMsg('success', '条文を削除しました');
    } catch (err) {
      showMsg('error', err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleMoveArticle = async (index, direction) => {
    const newArticles = [...articles];
    const swapIdx = direction === 'up' ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= newArticles.length) return;

    [newArticles[index], newArticles[swapIdx]] = [newArticles[swapIdx], newArticles[index]];
    const orders = newArticles.map((a, i) => ({ id: a.id, order: (i + 1) * 10 }));

    setArticles(newArticles);
    try {
      const res = await fetch(buildApiUrl(`/api/admin/legal-documents/${docType}/articles-reorder`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ orders }),
      });
      if (!res.ok) throw new Error((await res.json()).message);
    } catch (err) {
      showMsg('error', err.message);
      fetchArticles();
    }
  };

  // ============================================================
  // サブセクション
  // ============================================================

  const handleAddSubArticle = (article) => {
    const subs = [...(article.subArticles || []), { heading: '', body: '' }];
    handleUpdateArticle(article.id, { subArticles: subs });
    setArticles(prev => prev.map(a => a.id === article.id ? { ...a, subArticles: subs } : a));
  };

  const handleUpdateSubArticle = (article, subIdx, field, value) => {
    const subs = [...(article.subArticles || [])];
    subs[subIdx] = { ...subs[subIdx], [field]: value };
    setArticles(prev => prev.map(a => a.id === article.id ? { ...a, subArticles: subs } : a));
  };

  const handleSaveSubArticles = (article) => {
    handleUpdateArticle(article.id, { subArticles: article.subArticles || [] });
  };

  const handleDeleteSubArticle = (article, subIdx) => {
    const subs = [...(article.subArticles || [])];
    subs.splice(subIdx, 1);
    handleUpdateArticle(article.id, { subArticles: subs });
    setArticles(prev => prev.map(a => a.id === article.id ? { ...a, subArticles: subs } : a));
  };

  // ============================================================
  // バージョン作成・公開切り替え
  // ============================================================

  const handleOpenVersionModal = async () => {
    setActionLoading(true);
    setDiffPreviewError('');
    try {
      const res = await fetch(buildApiUrl(`/api/admin/legal-documents/${docType}/diff-preview`), { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || '差分プレビューの取得に失敗しました');
      setDiffPreview(data);
      setVersionForm({
        changeType: 'minor',
        effectiveDate: new Date().toISOString().slice(0, 10),
        diffSummary: data.diffSummary || '',
        publishNow: false,
      });
      setShowVersionModal(true);
    } catch (err) {
      const message = err.message || '差分プレビューの取得に失敗しました';
      setDiffPreviewError(message);
      showMsg('error', message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateVersion = async () => {
    if (!versionForm.effectiveDate) {
      showMsg('error', '施行日は必須です');
      return;
    }
    const nextVer = versionForm.changeType === 'major' ? diffPreview?.nextMajor : diffPreview?.nextMinor;
    const ok = await showConfirm({
      title: 'バージョン登録',
      message: `${DOC_TYPE_LABELS[docType]} を ${nextVer} として登録します。${versionForm.publishNow ? '\n同時に公開されます。' : ''}\nよろしいですか？`,
      confirmLabel: '登録する',
    });
    if (!ok) return;

    setActionLoading(true);
    setMsg({ type: '', text: '' });
    try {
      const res = await fetch(buildApiUrl(`/api/admin/legal-documents/${docType}/create-version`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(versionForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      const pubText = data.published ? '（公開済み）' : '（未公開）';
      showMsg('success', `${data.version} を登録しました${pubText}（${data.articleCount}条）`);
      setShowVersionModal(false);
      await Promise.all([fetchArticles(), fetchVersions(), fetchDiffPreview()]);
    } catch (err) {
      showMsg('error', err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetPublished = async (versionId, versionStr) => {
    const ok = await showConfirm({
      title: '公開バージョンの切替',
      message: `${versionStr} を公開中バージョンに切り替えます。\nユーザーに表示される規約内容が即時変更されます。よろしいですか？`,
      confirmLabel: '切り替える',
      danger: true,
    });
    if (!ok) return;
    setActionLoading(true);
    setMsg({ type: '', text: '' });
    try {
      const res = await fetch(buildApiUrl(`/api/admin/legal-documents/${docType}/versions/${versionId}/set-published`), {
        method: 'PUT',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      showMsg('success', `${data.version} を公開中に切り替えました`);
      await Promise.all([fetchArticles(), fetchVersions(), fetchDiffPreview()]);
    } catch (err) {
      showMsg('error', err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // ============================================================
  // 通知
  // ============================================================

  const handleNotify = async (versionId) => {
    const ok = await showConfirm({
      title: '改定通知メール',
      message: '全クリニックに改定通知メールを送信します。\nこの操作は取り消せません。よろしいですか？',
      confirmLabel: '送信する',
      danger: true,
    });
    if (!ok) return;
    setActionLoading(true);
    setMsg({ type: '', text: '' });
    try {
      const res = await fetch(buildApiUrl(`/api/admin/legal-documents/${docType}/versions/${versionId}/notify`), {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      showMsg('success', `通知完了: 送信 ${data.sent}件 / スキップ ${data.skipped}件 / 失敗 ${data.failed}件`);
      await fetchVersions();
    } catch (err) {
      showMsg('error', err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // ============================================================
  // ローカル編集ステート
  // ============================================================

  const handleLocalHeadingChange = (id, value) => {
    setArticles(prev => prev.map(a => a.id === id ? { ...a, heading: value } : a));
  };

  const handleLocalBodyChange = (id, value) => {
    setArticles(prev => prev.map(a => a.id === id ? { ...a, body: value } : a));
  };

  const handleSaveArticle = async (article) => {
    setActionLoading(true);
    try {
      await handleUpdateArticle(article.id, {
        heading: article.heading,
        body: article.body,
        subArticles: article.subArticles || [],
      });
      showMsg('success', `「${article.heading}」を保存しました`);
      fetchDiffPreview();
    } catch { /* handled inside */ } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    if (typeof d === 'string') return d;
    return new Date(d).toLocaleString('ja-JP');
  };

  // ============================================================
  // レンダリング
  // ============================================================

  const renderArticleEditor = () => (
    <>
      {articles.length === 0 && !loading && (
        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>
          条文がまだありません。「条文を追加」ボタンから追加してください。
        </p>
      )}

      {articles.map((a, idx) => (
        <div key={a.id} style={S.articleCard}>
          <div style={S.articleHandle}>
            <span style={S.orderBadge}>{idx + 1}</span>
            <input
              type="text"
              value={a.heading}
              onChange={(e) => handleLocalHeadingChange(a.id, e.target.value)}
              onBlur={() => handleSaveArticle(a)}
              style={{ ...S.input, flex: 1, fontWeight: 600 }}
              placeholder="条文見出し"
            />
            <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
              <button
                onClick={() => handleMoveArticle(idx, 'up')}
                disabled={idx === 0}
                style={{ ...S.btnSmall, background: idx === 0 ? 'var(--color-bg-hover)' : 'var(--color-bg-active)', color: idx === 0 ? 'var(--color-text-muted)' : 'var(--color-text-primary)', cursor: idx === 0 ? 'not-allowed' : 'pointer' }}
                title="上へ移動"
                aria-label="上へ移動"
              ><IconChevronUp size={14} /></button>
              <button
                onClick={() => handleMoveArticle(idx, 'down')}
                disabled={idx === articles.length - 1}
                style={{ ...S.btnSmall, background: idx === articles.length - 1 ? 'var(--color-bg-hover)' : 'var(--color-bg-active)', color: idx === articles.length - 1 ? 'var(--color-text-muted)' : 'var(--color-text-primary)', cursor: idx === articles.length - 1 ? 'not-allowed' : 'pointer' }}
                title="下へ移動"
                aria-label="下へ移動"
              ><IconChevronDown size={14} /></button>
              <button
                onClick={() => handleDeleteArticle(a.id, a.heading)}
                disabled={actionLoading}
                style={{ ...S.btnSmall, background: 'var(--color-accent-danger)', color: '#fff' }}
                title="削除"
              >✕</button>
            </div>
          </div>

          <textarea
            value={a.body}
            onChange={(e) => handleLocalBodyChange(a.id, e.target.value)}
            onBlur={() => handleSaveArticle(a)}
            rows={4}
            style={S.textarea}
            placeholder="条文の本文を入力してください"
          />

          {/* サブセクション */}
          {(a.subArticles || []).length > 0 && (
            <div style={{ marginTop: '0.75rem', paddingLeft: '1.5rem', borderLeft: '2px solid var(--color-border)' }}>
              {a.subArticles.map((sub, si) => (
                <div key={si} style={{ marginBottom: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <input
                      type="text"
                      value={sub.heading}
                      onChange={(e) => handleUpdateSubArticle(a, si, 'heading', e.target.value)}
                      onBlur={() => handleSaveSubArticles(a)}
                      style={{ ...S.input, flex: 1, fontSize: '0.85rem' }}
                      placeholder="サブセクション見出し"
                    />
                    <button
                      onClick={() => handleDeleteSubArticle(a, si)}
                      style={{ ...S.btnSmall, background: 'var(--color-accent-danger)', color: '#fff' }}
                      title="サブセクション削除"
                    >✕</button>
                  </div>
                  <textarea
                    value={sub.body}
                    onChange={(e) => handleUpdateSubArticle(a, si, 'body', e.target.value)}
                    onBlur={() => handleSaveSubArticles(a)}
                    rows={2}
                    style={{ ...S.textarea, fontSize: '0.85rem' }}
                    placeholder="サブセクション本文"
                  />
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '6px' }}>
            <button
              onClick={() => handleAddSubArticle(a)}
              style={{ ...S.btnSmall, background: 'var(--color-bg-active)', color: 'var(--color-accent-secondary)' }}
            >
              + サブセクション
            </button>
            <button
              onClick={() => handleSaveArticle(a)}
              disabled={actionLoading}
              style={{ ...S.btnSmall, background: 'var(--color-accent-secondary)', color: '#fff' }}
            >
              保存
            </button>
          </div>
        </div>
      ))}

      <button
        onClick={handleAddArticle}
        disabled={actionLoading}
        style={{
          ...S.btnPrimary(actionLoading),
          width: '100%',
          padding: '12px',
          marginTop: '0.5rem',
          border: '2px dashed var(--color-border)',
          background: actionLoading ? 'var(--color-bg-hover)' : 'transparent',
          color: actionLoading ? 'var(--color-text-muted)' : 'var(--color-accent-secondary)',
          fontWeight: 600,
        }}
      >
        + 条文を追加
      </button>
    </>
  );

  const renderPublishPanel = () => (
    <div style={S.card}>
      <h3 style={S.cardTitle}>公開設定</h3>

      {meta.lastPublishedVersion && (
        <div style={{ marginBottom: '1rem', padding: '0.75rem', borderRadius: 'var(--radius-md)', background: 'var(--color-bg-primary)', border: '1px solid var(--color-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={S.badge('rgba(22, 163, 74, 0.12)', '#15803d')}>公開中</span>
            <span style={{ fontSize: '0.85rem', color: 'var(--color-text-primary)', fontWeight: 600 }}>{meta.lastPublishedVersion}</span>
            <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>公開日: {formatDate(meta.lastPublishedAt)}</span>
          </div>
        </div>
      )}

      {diffPreview && (
        <div style={{
          padding: '0.75rem 1rem',
          borderRadius: 'var(--radius-md)',
          marginBottom: '1rem',
          background: diffPreview.hasChanges ? 'rgba(217, 119, 6, 0.08)' : 'rgba(22, 163, 74, 0.08)',
          border: `1px solid ${diffPreview.hasChanges ? 'rgba(217, 119, 6, 0.25)' : 'rgba(22, 163, 74, 0.25)'}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <span style={{
              fontSize: '0.82rem',
              fontWeight: 600,
              color: diffPreview.hasChanges ? '#b45309' : '#15803d',
            }}>
              {diffPreview.hasChanges ? '未登録の変更があります' : '公開中と同一です'}
            </span>
            <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
              現在: {diffPreview.currentCount}条 / 公開中: {diffPreview.prevCount}条
            </span>
          </div>
          {diffPreview.hasChanges && (
            <pre style={{
              margin: 0, fontSize: '0.8rem', color: 'var(--color-text-secondary)', lineHeight: 1.6,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              {diffPreview.diffSummary}
            </pre>
          )}
        </div>
      )}

      {diffPreviewError && (
        <div style={{
          padding: '0.75rem 1rem',
          borderRadius: 'var(--radius-md)',
          marginBottom: '1rem',
          background: 'rgba(220, 38, 38, 0.08)',
          border: '1px solid rgba(220, 38, 38, 0.25)',
          color: 'var(--color-accent-danger)',
          fontSize: '0.85rem',
          fontWeight: 600,
        }}>
          差分プレビューを取得できませんでした: {diffPreviewError}
        </div>
      )}

      <button
        onClick={handleOpenVersionModal}
        disabled={actionLoading || articles.length === 0 || (diffPreview && !diffPreview.hasChanges)}
        style={S.btnPrimary(actionLoading || articles.length === 0 || (diffPreview && !diffPreview.hasChanges))}
      >
        新バージョンとして登録
      </button>
    </div>
  );

  const renderVersionModal = () => {
    if (!showVersionModal || !diffPreview) return null;
    const nextVer = versionForm.changeType === 'major' ? diffPreview.nextMajor : diffPreview.nextMinor;
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }} onClick={() => setShowVersionModal(false)}>
        <div style={{
          background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-lg)', padding: '2rem',
          width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto',
          border: '1px solid var(--color-border)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }} onClick={(e) => e.stopPropagation()}>
          <h3 style={{ margin: '0 0 1.25rem', fontSize: '1.1rem', fontWeight: 700, color: 'var(--color-text-primary)' }}>
            新バージョン登録
          </h3>

          <div style={{ marginBottom: '1.25rem' }}>
            <label style={S.label}>変更の種類</label>
            <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
              {[
                { value: 'minor', label: 'マイナーチェンジ', ver: diffPreview.nextMinor, desc: '軽微な修正・文言変更' },
                { value: 'major', label: 'メジャーチェンジ', ver: diffPreview.nextMajor, desc: '条文の追加・削除・大幅改定' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setVersionForm(prev => ({ ...prev, changeType: opt.value }))}
                  style={{
                    flex: 1, padding: '12px', borderRadius: 'var(--radius-md)', cursor: 'pointer', textAlign: 'left',
                    border: versionForm.changeType === opt.value
                      ? '2px solid var(--color-accent-secondary)'
                      : '1px solid var(--color-border)',
                    background: versionForm.changeType === opt.value
                      ? 'rgba(66, 133, 244, 0.08)'
                      : 'var(--color-bg-tertiary)',
                  }}
                >
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '2px' }}>
                    {opt.label}
                  </div>
                  <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--color-accent-secondary)', marginBottom: '2px' }}>
                    {opt.ver}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={S.label}>施行日 *</label>
            <input
              type="date"
              value={versionForm.effectiveDate}
              onChange={(e) => setVersionForm(prev => ({ ...prev, effectiveDate: e.target.value }))}
              style={S.input}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={S.label}>変更概要（自動生成 — 編集可能）</label>
            <textarea
              value={versionForm.diffSummary}
              onChange={(e) => setVersionForm(prev => ({ ...prev, diffSummary: e.target.value }))}
              rows={4}
              style={S.textarea}
            />
          </div>

          <div style={{
            marginBottom: '1.25rem', padding: '10px 14px', borderRadius: 'var(--radius-md)',
            background: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)',
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--color-text-primary)' }}>
              <input
                type="checkbox"
                checked={versionForm.publishNow}
                onChange={(e) => setVersionForm(prev => ({ ...prev, publishNow: e.target.checked }))}
                style={{ width: '16px', height: '16px', accentColor: 'var(--color-accent-secondary)' }}
              />
              <span style={{ fontWeight: 600 }}>同時に公開する</span>
            </label>
            <p style={{ margin: '4px 0 0 24px', fontSize: '0.75rem', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
              チェックすると、バージョン登録と同時にユーザーに表示される規約が切り替わります。
            </p>
          </div>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowVersionModal(false)}
              style={{ ...S.btnSmall, padding: '8px 20px', background: 'transparent', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)', fontSize: '0.85rem' }}
            >
              キャンセル
            </button>
            <button
              onClick={handleCreateVersion}
              disabled={actionLoading}
              style={{ ...S.btnPrimary(actionLoading), padding: '8px 24px' }}
            >
              {actionLoading ? '登録中...' : `${nextVer} として登録`}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderVersionHistory = () => (
    <div>
      <h3 style={{ ...S.cardTitle, marginTop: '0.5rem' }}>バージョン履歴</h3>
      {versionsLoading ? (
        <AdminTableShimmer rows={4} columns={3} />
      ) : versions.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>まだ登録されたバージョンはありません。</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {versions.map(v => (
            <div key={v.id} style={{
              border: v.isPublished
                ? '2px solid var(--color-accent-secondary)'
                : '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              padding: '1rem 1.25rem',
              background: v.isPublished ? 'rgba(66, 133, 244, 0.04)' : 'var(--color-bg-secondary)',
            }}>
              <div className="admin-legal-version-row">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
                    <span style={{ fontSize: '0.85rem', color: 'var(--color-text-primary)', fontWeight: 700 }}>{v.version}</span>
                    {v.isPublished && (
                      <span style={S.badge('rgba(52, 168, 83, 0.18)', '#3fb950')}>公開中</span>
                    )}
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>施行日: {v.effectiveDate}</span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{v.articleCount}条</span>
                    {v.notified ? (
                      <span style={S.badge('rgba(52, 168, 83, 0.15)', '#3fb950')}>通知済</span>
                    ) : (
                      <span style={S.badge('rgba(210, 153, 34, 0.15)', '#d29922')}>未通知</span>
                    )}
                  </div>
                  <p style={{ margin: '0', fontSize: '0.85rem', color: 'var(--color-text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {v.diffSummary}
                  </p>
                  <div style={S.meta}>
                    登録: {formatDate(v.createdAt)}
                    {v.notified && v.notifiedAt && (
                      <span style={{ marginLeft: '12px' }}>
                        通知: {formatDate(v.notifiedAt)}（{v.notifiedCount || 0}件送信）
                      </span>
                    )}
                  </div>
                </div>
                <div className="admin-legal-version-actions">
                  {!v.isPublished && (
                    <button
                      onClick={() => handleSetPublished(v.id, v.version)}
                      disabled={actionLoading}
                      style={S.btnOutline(actionLoading)}
                    >
                      公開する
                    </button>
                  )}
                  {v.notified ? (
                    <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>通知済み</span>
                  ) : (
                    <button onClick={() => handleNotify(v.id)} disabled={actionLoading} style={S.btnNotify(actionLoading)}>
                      通知を送信
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="admin-legal-documents">
      <h2 className="section-title">規約・ポリシー CMS</h2>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', marginBottom: '1rem', lineHeight: 1.6 }}>
        条文を編集し、バージョンとして登録・公開できます。ユーザーに表示されるのは公開中バージョンのスナップショットです。
      </p>

      {/* docType 切り替え */}
      <div className="admin-legal-doc-tabs">
        {Object.entries(DOC_TYPE_LABELS).map(([key, label]) => (
          <button key={key} onClick={() => setDocType(key)} style={S.tab(docType === key)}>
            {label}
          </button>
        ))}
      </div>

      {msg.text && (
        <div style={{
          padding: '12px 16px',
          borderRadius: 'var(--radius-md)',
          marginBottom: '1rem',
          background: msg.type === 'success' ? 'rgba(22, 163, 74, 0.1)' : 'rgba(220, 38, 38, 0.08)',
          border: `1px solid ${msg.type === 'success' ? 'rgba(22, 163, 74, 0.25)' : 'rgba(220, 38, 38, 0.2)'}`,
          color: msg.type === 'success' ? '#15803d' : '#b91c1c',
          fontSize: '0.9rem',
          fontWeight: 500,
        }}>
          {msg.text}
        </div>
      )}

      {loading ? (
        <AdminFormPageShimmer fields={8} withHeader={false} />
      ) : (
        <>
          {/* 条文エディタ */}
          <div style={S.card}>
            <h3 style={S.cardTitle}>条文エディタ（{articles.length}条）</h3>
            {renderArticleEditor()}
          </div>

          {/* 公開パネル */}
          {renderPublishPanel()}

          {/* バージョン履歴 */}
          {renderVersionHistory()}
        </>
      )}

      {/* バージョン登録モーダル */}
      {renderVersionModal()}
    </div>
  );
};

export default AdminLegalDocuments;
